import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaSearch, FaBars, FaTimes, FaRegHeart, FaChevronLeft, FaChevronRight, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext.tsx';
import { getCourseTemplates, createCourseFromTemplateById } from '../context/engine.ts';
import {
    getCategoryColor,
    inferCategoryFromName,
    getAllCategories
} from '../utils/categoryMapper';
import '../styles/courseGallery.css';

function CourseGallery({ setCurrentView, setCurrentCourseId }) {
    const { user } = useAuth();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [sortBy, setSortBy] = useState('popularity');
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState(null);
    const [creatingCourse, setCreatingCourse] = useState(null);
    const [coursesByCategory, setCoursesByCategory] = useState({});
    const [scrollState, setScrollState] = useState({});

    const observer = useRef();
    const scrollContainerRefs = useRef({});

    const lastCourseElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Fetch courses
    const fetchCourses = useCallback(async (resetPage = false) => {
        try {
            setLoading(true);
            setError(null);

            const currentPage = resetPage ? 0 : page;
            const offset = currentPage * 20;

            const { templates, total } = await getCourseTemplates({
                searchQuery: searchQuery || undefined,
                category: selectedCategory !== 'All' ? selectedCategory : undefined,
                sortBy,
                limit: 20,
                offset
            });

            if (resetPage) {
                setCourses(templates);
                setPage(0);
            } else {
                setCourses(prev => [...prev, ...templates]);
            }

            setTotalCount(total);
            setHasMore(offset + templates.length < total);
        } catch (err) {
            console.error('Error fetching courses:', err);
            setError('Failed to load courses. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedCategory, sortBy, page]);

    // Initial load and filter changes
    useEffect(() => {
        fetchCourses(true);
    }, [searchQuery, selectedCategory, sortBy]);

    // Load more on page change
    useEffect(() => {
        if (page > 0) {
            fetchCourses(false);
        }
    }, [page]);

    // Handle course selection
    const handleCourseClick = async (template) => {
        if (!user) {
            setCurrentView('signin');
            return;
        }

        if (creatingCourse === template.id) return;

        try {
            setCreatingCourse(template.id);
            const course = await createCourseFromTemplateById(template.id, user.id);
            setCurrentCourseId(course.id);
            setCurrentView('learn');
        } catch (err) {
            console.error('Error creating course:', err);
            alert('Failed to open course. Please try again.');
        } finally {
            setCreatingCourse(null);
        }
    };

    // Get display category
    const getDisplayCategory = (course) => {
        return course.category || inferCategoryFromName(course.original_name);
    };

    // Organize courses by category
    useEffect(() => {
        const organized = {};
        courses.forEach(course => {
            const category = getDisplayCategory(course);
            if (!organized[category]) {
                organized[category] = [];
            }
            organized[category].push(course);
        });
        setCoursesByCategory(organized);
    }, [courses]);

    // Check if container has scroll overflow
    const checkScrollOverflow = (category) => {
        const container = scrollContainerRefs.current[category];
        if (!container) return { canScrollLeft: false, canScrollRight: false };

        const canScrollLeft = container.scrollLeft > 0;
        const canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1);

        return { canScrollLeft, canScrollRight };
    };

    // Update scroll state for a category
    const updateScrollState = (category) => {
        const state = checkScrollOverflow(category);
        setScrollState(prev => ({
            ...prev,
            [category]: state
        }));
    };

    // Update scroll states when categories change
    useEffect(() => {
        const categories = Object.keys(coursesByCategory);
        const timer = setTimeout(() => {
            categories.forEach(category => {
                updateScrollState(category);
            });
        }, 100); // Small delay to ensure DOM is updated

        return () => clearTimeout(timer);
    }, [coursesByCategory]);

    // Update scroll states on window resize
    useEffect(() => {
        const handleResize = () => {
            Object.keys(coursesByCategory).forEach(category => {
                updateScrollState(category);
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [coursesByCategory]);

    // Scroll functions for horizontal navigation
    const scroll = (category, direction) => {
        const container = scrollContainerRefs.current[category];
        if (container) {
            const scrollAmount = container.offsetWidth * 0.8;
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });

            // Update scroll state after animation
            setTimeout(() => updateScrollState(category), 350);
        }
    };

    // Get categories to display based on filter
    const getDisplayCategories = () => {
        const allCategories = Object.keys(coursesByCategory);
        if (selectedCategory === 'All') {
            return allCategories;
        }
        return allCategories.filter(cat => cat === selectedCategory);
    };

    return (
        <div className="course-gallery">
            {/* Back Button */}
            <button 
                className="gallery-back-btn"
                onClick={() => setCurrentView('landing')}
                aria-label="Back to landing page"
            >
                <FaArrowLeft /> Back
            </button>

            {/* Header */}
            <div className="gallery-header">
                <h1>Course Gallery</h1>
                <p className="gallery-subtitle">
                    Explore our free courses
                </p>
            </div>

            {/* Search and Filter Bar */}
            <div className="gallery-controls">
                <div className="search-container">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search for a course"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <button
                    className="filter-toggle"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-label="Toggle filters"
                >
                    {showFilters ? <FaTimes /> : <FaBars />}
                </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="filters-panel">
                    <div className="filter-group">
                        <label>Category</label>
                        <div className="category-chips">
                            <button
                                className={`category-chip ${selectedCategory === 'All' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('All')}
                            >
                                All
                            </button>
                            {getAllCategories().map(category => (
                                <button
                                    key={category}
                                    className={`category-chip ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category)}
                                    style={{
                                        backgroundColor: selectedCategory === category ? getCategoryColor(category) : 'transparent',
                                        borderColor: getCategoryColor(category),
                                        color: selectedCategory === category ? '#000' : getCategoryColor(category)
                                    }}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="filter-group">
                        <label>Sort By</label>
                        <div className="sort-buttons">
                            <button
                                className={`sort-btn ${sortBy === 'popularity' ? 'active' : ''}`}
                                onClick={() => setSortBy('popularity')}
                            >
                                Most Popular
                            </button>
                            <button
                                className={`sort-btn ${sortBy === 'newest' ? 'active' : ''}`}
                                onClick={() => setSortBy('newest')}
                            >
                                Newest
                            </button>
                            <button
                                className={`sort-btn ${sortBy === 'alphabetical' ? 'active' : ''}`}
                                onClick={() => setSortBy('alphabetical')}
                            >
                                A-Z
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Category Rows - Netflix Style */}
            <div className="categories-container">
                {getDisplayCategories().map((category) => (
                    <div key={category} className="category-section">
                        {/* Category Header */}
                        <div className="category-header">
                            <h2 className="category-title">{category}</h2>
                            <span className="category-count">
                                {coursesByCategory[category].length} {coursesByCategory[category].length === 1 ? 'course' : 'courses'}
                            </span>
                        </div>

                        {/* Scrollable Course Row */}
                        <div className="category-row-wrapper">
                            {/* Left Arrow */}
                            {scrollState[category]?.canScrollLeft && (
                                <button
                                    className="scroll-arrow scroll-arrow-left"
                                    onClick={() => scroll(category, 'left')}
                                    aria-label="Scroll left"
                                >
                                    <FaChevronLeft />
                                </button>
                            )}

                            {/* Course Cards Container */}
                            <div
                                className="category-row"
                                ref={(el) => {
                                    scrollContainerRefs.current[category] = el;
                                    if (el) {
                                        // Update scroll state when ref is set
                                        setTimeout(() => updateScrollState(category), 50);
                                    }
                                }}
                                onScroll={() => updateScrollState(category)}
                            >
                                {coursesByCategory[category].map((course, index) => {
                                    const isLastElement = index === coursesByCategory[category].length - 1 &&
                                                         category === getDisplayCategories()[getDisplayCategories().length - 1];

                                    return (
                                        <div
                                            key={course.id}
                                            ref={isLastElement ? lastCourseElementRef : null}
                                            className={`course-card ${creatingCourse === course.id ? 'loading' : ''}`}
                                            onClick={() => handleCourseClick(course)}
                                            style={{
                                                backgroundColor: getCategoryColor(category),
                                                cursor: creatingCourse === course.id ? 'wait' : 'pointer'
                                            }}
                                        >
                                            {/* Category Badge */}
                                            <div className="course-badge">
                                                {category}
                                            </div>

                                            {/* Heart Icon (Future: favorites feature) */}
                                            <button
                                                className="favorite-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // TODO: Implement favorites
                                                }}
                                                aria-label="Add to favorites"
                                            >
                                                <FaRegHeart style={{ color: '#F5F3ED' }} />
                                            </button>

                                            {/* Course Name */}
                                            <div className="course-name">
                                                {course.original_name}
                                            </div>

                                            {/* Course Description (shown on hover/tap) */}
                                            {course.short_description && (
                                                <div className="course-description">
                                                    {course.short_description}
                                                </div>
                                            )}

                                            {/* Loading Overlay */}
                                            {creatingCourse === course.id && (
                                                <div className="loading-overlay">
                                                    <div className="spinner"></div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Right Arrow */}
                            {scrollState[category]?.canScrollRight && (
                                <button
                                    className="scroll-arrow scroll-arrow-right"
                                    onClick={() => scroll(category, 'right')}
                                    aria-label="Scroll right"
                                >
                                    <FaChevronRight />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Loading Indicator */}
            {loading && page === 0 && (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>Loading courses...</p>
                </div>
            )}

            {/* Load More Indicator */}
            {loading && page > 0 && (
                <div className="load-more-indicator">
                    <div className="spinner-small"></div>
                </div>
            )}

            {/* Empty State */}
            {!loading && courses.length === 0 && (
                <div className="empty-state">
                    <h3>No courses found</h3>
                    <p>Try adjusting your search or filters</p>
                </div>
            )}

            {/* End of Results */}
            {!loading && !hasMore && courses.length > 0 && (
                <div className="end-message">
                    You've reached the end!
                </div>
            )}
        </div>
    );
}

export default CourseGallery;
